import { Monthly10k } from '@daymade/common-lib/constants';
import { CurrentUserProgressionBar } from 'components/monthly-giveaway/ProgressionBar';
import GenericSkeleton from 'components/skeleton/GenericSkeleton';
import { useMonthlyRaffleData } from 'hooks/draw/useMonthlyRaffleData';
import { useUserStore } from 'hooks/stores/useUserStore';
import { useIsMobile } from 'hooks/ui/useIsMobile';
import { observer } from 'mobx-react-lite';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { TSubscriptions } from 'stores/UserStore';
import { formatShortDate, getMonthName } from 'utils/draw/getMonthName';
import styles from './Monthly10kGiveaway.module.css';
import DashboardComponentHeader from './common/DashboardComponentHeader';
import DashboardComponentLink from './common/DashboardComponentLink';
import { LOADING_TIMEOUT } from './common/constants';

const Monthly10kGiveaway = observer(({ loading }: { loading?: boolean }) => {
  const { isMobile } = useIsMobile();
  const { subscriptions, initialized } = useUserStore();
  const { monthlyRaffleData } = useMonthlyRaffleData();
  const [subscription, setSubscription] = useState<TSubscriptions[number]>();
  const [monthly10kLoading, setMonthly10kLoading] = useState(true);

  useEffect(() => {
    const activeSubscription = subscriptions.find(
      (sub) =>
        sub.subscription.status === 'ACTIVE' ||
        sub.subscription.status === 'SNOOZED' ||
        sub.subscription.status === 'FAILING',
    );

    if (initialized && !loading) {
      setTimeout(() => {
        setMonthly10kLoading(false);
      }, LOADING_TIMEOUT);
    }

    if (!subscription) {
      setSubscription(activeSubscription);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptions, initialized, loading]);

  return (
    <>
      <div className={styles.container}>
        <div>
          <DashboardComponentHeader
            title="Monthly £10k giveaway"
            subtitle={
              subscription
                ? 'Bonus raffle included in your membership'
                : 'For Premium & Lite subscribers'
            }
            separator
            loading={monthly10kLoading}
          />
          {monthly10kLoading ? (
            <div className={styles.progressionSkeletons}>
              <GenericSkeleton
                containerClassName="flex-1"
                height={140}
                width={'100%'}
                borderRadius={isMobile ? 18 : 24}
              />
              <div className={styles.progressionSkeletonCircle}>
                <GenericSkeleton circle width={107} height={107} />
              </div>
            </div>
          ) : (
            <CurrentUserProgressionBar
              size="large"
              customProgressTitle={
                <p className={styles.introText + ' aboutUsTypographyCaption'}>
                  {subscription ? 'You' : 'Subscribe to'} earn{' '}
                  <span className="textOrange">
                    {Monthly10k.minimumEntriesPerWeek} entries
                  </span>{' '}
                  every week
                </p>
              }
            />
          )}
        </div>
        {monthly10kLoading ? (
          <GenericSkeleton
            height={'100%'}
            width={'100%'}
            borderRadius={isMobile ? 18 : 24}
          />
        ) : (
          <Link href={Monthly10k.urlPath}>
            <a className={styles.monthlyLink}>
              <div className={styles.prizeInfo}>
                {monthlyRaffleData?.prize && (
                  <div className={styles.prizeImageContainer}>
                    <img
                      src={monthlyRaffleData.prize.imageLocations.main}
                      alt={monthlyRaffleData.prize.seoImageAltText.main}
                    />
                  </div>
                )}
                <div className={styles.prizeTextContainer}>
                  <div>
                    <div className="aboutUsTypographyBodySmall">
                      {monthlyRaffleData?.drawDate
                        ? `${getMonthName(monthlyRaffleData.drawDate)} prize:`
                        : "This month's prize:"}
                    </div>
                    {monthlyRaffleData?.prize && (
                      <div
                        className={`aboutUsTypographyHeaderMedium ${styles.prizeTitle}`}
                      >
                        {monthlyRaffleData.prize.title}
                      </div>
                    )}
                  </div>
                  {monthlyRaffleData?.drawDate && (
                    <div
                      className={`${styles.drawDateBadge} aboutUsTypographyBodySmall`}
                    >
                      Draw date: {formatShortDate(monthlyRaffleData.drawDate)}
                    </div>
                  )}
                </div>
              </div>
            </a>
          </Link>
        )}
      </div>
      <DashboardComponentLink
        loading={monthly10kLoading}
        linkText="Learn more"
        href={Monthly10k.urlPath}
      />
    </>
  );
});

export default Monthly10kGiveaway;
